import { useQuery } from "@tanstack/vue-query";
import {
  EventSorting,
  EventStatus,
  ProductSorting,
  ProductStatus,
  type SearchApiV2SearchCategoriesGetRequest,
} from "mws-ts-v2-sdk";

import { queries } from "~/queries";
import {
  useInfiniteCategoriesSearchQuery,
  useInfiniteEventsSearchQuery,
  useInfiniteProductsSearchQuery,
} from "~/queries/catalog/search";
import {
  type CategorySearchResultTypes,
  SearchResultTypes,
} from "~/types/Search";

export const useAllSearchResultsQuery = (searchTerm: Ref<string>) => {
  const platformStore = usePlatformStore();
  const platformId = computed(
    () => platformStore.getPlatformConfiguration.platformId,
  );
  const params = computed(() => {
    return {
      xPlatformKey: platformId.value,
      searchTerm: unref(searchTerm),
      numberOfLeagueCategories: 10,
      numberOfOtherCategories: 10,
      numberOfTeamCategories: 10,
    };
  });

  return useQuery(queries.search.all(params));
};

type CategorySearchParams = {
  categorySearchType?: SearchApiV2SearchCategoriesGetRequest["categorySearchType"];
};

type EventSearchParams = {
  eventFiltersEventStatus?: EventStatus;
  eventFiltersCategoryIds?: string;
};

type ProductSearchParams = {
  productFiltersMinimumPrice?: number;
  productFiltersMaximumPrice?: number;
  productFiltersCurrencyCode?: string;
  productFiltersProductStatus?: ProductStatus;
  productFiltersCategoryIds?: string;
};

type SearchParams = {
  xPlatformKey: string;
  searchTerm: string;
} & (CategorySearchParams | EventSearchParams | ProductSearchParams);

export const useTabSearchResults = (options: {
  searchTerm: Ref<string>;
  tab: Ref<SearchResultTypes>;
  filters: Ref<{
    categoryIds?: string[];
    productStatus?: ProductStatus;
    eventStatus?: EventStatus;
    priceFrom?: string;
    priceTo?: string;
  }>;
  sorting: Ref<EventSorting | ProductSorting | undefined>;
  activeCurrency?: Ref<string>;
}) => {
  const { searchTerm, tab, filters, sorting, activeCurrency } = options;
  const platformStore = usePlatformStore();
  const platformId = computed(
    () => platformStore.getPlatformConfiguration.platformId,
  );

  const isCategoryTabActive = (
    tab: SearchResultTypes,
  ): tab is CategorySearchResultTypes => {
    return [
      SearchResultTypes.Teams,
      SearchResultTypes.Leagues,
      SearchResultTypes.Other,
    ].includes(tab);
  };

  const searchParams = computed<SearchParams>(() => {
    const commonParams = {
      xPlatformKey: platformId.value,
      searchTerm: unref(searchTerm),
    };

    if (isCategoryTabActive(tab.value)) {
      return {
        ...commonParams,
        categorySearchType: tab.value,
      };
    }

    if (tab.value === SearchResultTypes.Events) {
      return {
        ...commonParams,
        eventFiltersEventStatus: filters.value.eventStatus,
        eventFiltersCategoryIds: filters.value.categoryIds?.join(","),
        eventSorting: sorting.value,
      };
    }

    if (tab.value === SearchResultTypes.Products) {
      return {
        ...commonParams,
        productFiltersMinimumPrice: filters.value.priceFrom
          ? Number(filters.value.priceFrom) * 100
          : undefined,
        productFiltersMaximumPrice: filters.value.priceTo
          ? Number(filters.value.priceTo) * 100
          : undefined,
        productFiltersProductStatus: filters.value.productStatus,
        productFiltersCategoryIds: filters.value.categoryIds?.join(","),
        productFiltersCurrencyCode: activeCurrency?.value,
        productSorting: sorting.value,
      };
    }

    return commonParams;
  });

  const eventsEnabled = computed(() => tab.value === SearchResultTypes.Events);
  const events = useInfiniteEventsSearchQuery(searchParams, eventsEnabled);

  const productsEnabled = computed(
    () => tab.value === SearchResultTypes.Products,
  );
  const products = useInfiniteProductsSearchQuery(
    searchParams,
    productsEnabled,
  );

  const categoriesEnabled = computed(() => isCategoryTabActive(tab.value));
  const categories = useInfiniteCategoriesSearchQuery(
    searchParams,
    categoriesEnabled,
  );

  // Typecast type enum because TypeScript does not understand narrowing down the type
  // and attaching it to the data type.
  return computed(() => {
    if (eventsEnabled.value) {
      return {
        type: SearchResultTypes.Events as SearchResultTypes.Events,
        hasNextPage: events.hasNextPage.value,
        fetchNextPage: events.fetchNextPage,
        data:
          events.data.value?.pages.flatMap((paginated) => paginated.results) ??
          [],
        count: events.data.value?.pages[0]?.totalCount || 0,
        isLoading: events.isLoading.value,
        isFetching: events.isFetching.value,
      };
    } else if (productsEnabled.value) {
      return {
        type: SearchResultTypes.Products as SearchResultTypes.Products,
        hasNextPage: products.hasNextPage.value,
        fetchNextPage: products.fetchNextPage,
        data:
          products.data.value?.pages.flatMap(
            (paginated) => paginated.results,
          ) ?? [],
        count: products.data.value?.pages[0]?.totalCount || 0,
        isLoading: products.isLoading.value,
        isFetching: products.isFetching.value,
      };
    } else {
      return {
        type: tab.value as CategorySearchResultTypes,
        hasNextPage: categories.hasNextPage.value,
        fetchNextPage: categories.fetchNextPage,
        data:
          categories.data.value?.pages.flatMap(
            (paginated) => paginated.results,
          ) ?? [],
        count: categories.data.value?.pages[0]?.totalCount || 0,
        isLoading: categories.isLoading.value,
        isFetching: categories.isFetching.value,
      };
    }
  });
};
